import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

let isRefreshing = false;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    // Exclude the auth *mutation* endpoints from retry-via-refresh (retrying a failed login,
    // refresh, or logout makes no sense and refresh-on-refresh-failure would infinite loop),
    // but /auth/me is a plain read and deserves the same retry safety net as any other GET -
    // without this, a transient 401 on /auth/me (e.g. a cookie-propagation race in the moment
    // right after MFA verification sets it) permanently signals "logged out" with no recovery,
    // even though the session is actually fine and every other endpoint would have succeeded.
    const isNonRetryableAuthEndpoint = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/mfa"]
      .some((p) => original.url.includes(p));
    if (error.response?.status === 401 && !original._retry && !isNonRetryableAuthEndpoint) {
      original._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await apiClient.post("/auth/refresh");
          isRefreshing = false;
          return apiClient(original);
        } catch (refreshError) {
          isRefreshing = false;
          // The refresh token itself is gone/expired too - this session cannot be restored.
          // Nothing checks auth state again after the initial page load, so without this
          // signal the user is silently left on a page where every API call 401s forever.
          // AuthProvider listens for this and flips global user state to false, which
          // ProtectedRoute reacts to by redirecting to the login screen.
          sessionStorage.setItem("yoko_session_expired", "1");
          window.dispatchEvent(new CustomEvent("yoko:session-expired"));
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
